<script setup>
import { computed, ref } from 'vue'
import IconChevronLeft from '@/components/icon/IconChevronLeft.vue'
import IconChevronRight from '@/components/icon/IconChevronRight.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronRightDouble from '../icon/IconChevronRightDouble.vue'

const props = defineProps({
	currentPage: {
		type: Number,
		default: 0,
	},
	pages: {
		type: Number,
		default: 0,
	},
	ellipsisThreshold: {
		type: Number,
		default: 8,
	},
	variant: {
		type: String,
		default: 'default',
	},
})

const emit = defineEmits({
	goToPage: (value) => value !== undefined,
})

// Computed
const customClasses = computed(() => {
	if (props.variant === 'light') {
		return 'rounded-sm text-tiny size-7 hover:bg-surface-4 text-surface-text-1 hover:text-white disabled:bg-surface-3 flex items-center justify-center transition-all'
	}
	return 'rounded-sm text-tiny size-7 bg-surface-4 hover:bg-surface-5 text-surface-9 hover:text-white disabled:bg-surface-3 flex items-center justify-center transition-all'
})

const totalPages = computed(() => Math.max(0, props.pages ?? 0))

const pageSlots = computed(() => {
	const total = totalPages.value
	if (total <= 0) return []

	if (total <= props.ellipsisThreshold) {
		return Array.from({ length: total }, (_, i) => ({
			type: 'page',
			index: i,
			key: `page-${i}`,
		}))
	}

	const cp = props.currentPage ?? 0
	const windowStart = Math.max(1, cp - 1)
	const windowEnd = Math.min(total - 2, cp + 1)

	const slots = []
	let ellipsisId = 0
	const pushEllipsis = () => {
		ellipsisId += 1
		slots.push({ type: 'ellipsis', key: `ellipsis-${ellipsisId}` })
	}

	slots.push({ type: 'page', index: 0, key: 'page-0' })

	if (windowStart > 1) {
		pushEllipsis()
	}

	for (let i = windowStart; i <= windowEnd; i++) {
		slots.push({ type: 'page', index: i, key: `page-${i}` })
	}

	if (windowEnd < total - 2) {
		pushEllipsis()
	}

	if (total > 1) {
		slots.push({ type: 'page', index: total - 1, key: `page-${total - 1}` })
	}

	return slots
})

const goToPage = (page) => emit('goToPage', page)
</script>

<template>
	<ul class="flex flex-wrap items-center gap-1 justify-center !p-1 rounded-md" :class="props.variant === 'default' ? 'bg-surface-3' : ''">
		<li>
			<FormButton :disabled="currentPage === 0 || pages <= 1" :customClasses="customClasses" @click="goToPage(0)">
				<IconBase class="rotate-180" fill="none">
					<IconChevronRightDouble />
				</IconBase>
			</FormButton>
		</li>
		<li>
			<FormButton :disabled="currentPage === 0 || pages <= 1" :customClasses="customClasses" @click="goToPage(currentPage - 1)">
				<IconBase fill="none">
					<IconChevronLeft />
				</IconBase>
			</FormButton>
		</li>
		<li v-for="slot in pageSlots" :key="slot.key">
			<span v-if="slot.type === 'ellipsis'" class="text-tiny text-surface-7 px-1 select-none inline-flex items-center justify-center size-7">
				…
			</span>
			<FormButton
				v-else
				:disabled="slot.index === currentPage"
				:customClasses="customClasses"
				:label="String(slot.index + 1)"
				@click="goToPage(slot.index)"
			/>
		</li>
		<li>
			<FormButton :disabled="currentPage >= pages - 1 || pages <= 1" :customClasses="customClasses" @click="goToPage(currentPage + 1)">
				<IconBase fill="none">
					<IconChevronRight />
				</IconBase>
			</FormButton>
		</li>
		<li>
			<FormButton :disabled="currentPage >= pages - 1 || pages <= 1" :customClasses="customClasses" @click="goToPage(pages - 1)">
				<IconBase fill="none">
					<IconChevronRightDouble />
				</IconBase>
			</FormButton>
		</li>
	</ul>
</template>
